// @flow
import { css } from "styled-components";
import { useInView } from "react-intersection-observer";
import { media } from "@nested/brand";
import { ButtonLink } from "@nested/component-library";
import { ResponsiveImage } from "../../components/ResponsiveImage";
import {
  sectionHeading,
  largeHeading,
  regularHeading,
} from "../../components/Typography/Headings";
import { mediumParagraph } from "../../components/Typography/Paragraphs";
import { variableSliceAdjacentSliceRules } from "../AdjacentSliceRules/AdjacentSliceRules";

const backgroundStyle = css`
  background-color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.hague : "white"};
  ${variableSliceAdjacentSliceRules}
`;

const wrapperStyle = css`
  margin: 0 auto;
  text-align: center;
  align-items: stretch;
  display: flex;
  flex-direction: column;
  ${media.tablet`
    text-align: left;
    max-width: 534px;
  `}
  ${media.desktop`
    flex-direction: ${(props) =>
      props.reversed === "Yes" ? "row-reverse" : "row"};
    align-items: center;
    justify-content: space-between;
    max-width: 1140px;
  `};
`;

const imageWrapperStyle = css`
  ${media.desktop`
    width: calc((100% * 2 / 3) - 13px);
    box-sizing: border-box;
  `};
`;

const iconWrapperStyle = css`
  display: none;
  ${media.desktop`
    margin-bottom: 10px;
    display: block;
  `};
`;

const imageStyle = css`
  width: 100%;
`;

const iconStyle = css`
  width: 100px;
`;

const contentWrapperStyle = css`
  margin-top: 20px;
  ${media.desktop`
    width: calc((100% / 3) - 27px);
    box-sizing: border-box;
    margin-top: 0px;
  `};
`;

const headingStyle = css`
  white-space: pre-wrap;
  margin-top: 10px;
  margin-bottom: 20px;
  ${regularHeading};
  color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.sand100 : theme.palette.hague};
  ${media.tablet`
    ${largeHeading}
    max-width: 100%;
    color: ${({ darkMode, theme }) =>
      darkMode ? theme.palette.sand100 : theme.palette.hague};
  `}
`;

const subHeadingStyle = css`
  white-space: pre-wrap;
  margin: 0;
  ${sectionHeading};
  color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.sand100 : theme.palette.hague80};
  ${media.tablet`
      margin-top: 0px;
  `}
`;

const paragraphStyle = css`
  ${mediumParagraph};
  color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.sand100 : theme.palette.hague};
  margin-top: 20px;
  margin-bottom: 0px;
`;

const buttonWrapperStyle = css`
  margin-top: 30px;
  ${media.tablet`
    margin-top: 20px;
  `};
`;

type Props = {
  buttonLabel: string,
  buttonLink: PrismicLink,
  darkMode: "Yes" | "No",
  heading: string,
  icon: PrismicImage,
  image: PrismicImage,
  paragraph: string,
  reversed: "Yes" | "No",
  subHeading: string,
};

export const LargeImageSmallText = ({
  buttonLabel,
  buttonLink,
  darkMode,
  heading,
  icon,
  image,
  paragraph,
  reversed,
  subHeading,
}: Props) => {
  const darkModeBool = darkMode === "Yes";
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0,
  });
  return (
    <div
      ref={ref}
      css={backgroundStyle}
      darkMode={darkModeBool}
      className={`optional-top-padding ${
        darkModeBool ? "darkModeOn" : "darkModeOff"
      }`}
    >
      <div reversed={reversed} css={wrapperStyle}>
        <div css={imageWrapperStyle}>
          <ResponsiveImage
            inView={inView}
            css={imageStyle}
            src={image.url}
            alt={image.alt}
          />
        </div>
        <div css={contentWrapperStyle}>
          {icon.url && (
            <div showOnDesktop={Boolean(icon)} css={iconWrapperStyle}>
              <ResponsiveImage
                inView={inView}
                css={iconStyle}
                src={icon.url}
                alt={icon.alt}
              />
            </div>
          )}
          {subHeading && (
            <p css={subHeadingStyle} darkMode={darkModeBool}>
              {subHeading}
            </p>
          )}
          <h3 css={headingStyle} darkMode={darkModeBool}>
            {heading}
          </h3>
          <p css={paragraphStyle} darkMode={darkModeBool}>
            {paragraph}
          </p>
          {buttonLink.url && buttonLabel && (
            <div css={buttonWrapperStyle}>
              <ButtonLink
                colour="sand"
                to={buttonLink.url}
                target={buttonLink?.target}
              >
                {buttonLabel}
              </ButtonLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
